/**
 * L2 experience derivation (Epic 3 STATE-MACHINE v1.1 §5).
 *
 * experienceState = derive(attempt, connectivity, tabState, localIntent, now)
 *
 * Properties (binding):
 * - Total over declared inputs
 * - Idempotent
 * - No I/O and no clock read (`now` is an input)
 *
 * AO-3.1 callers pass tabState = TAB_NONE and do not drive T3–T6.
 */

export const FLOW_STATE = Object.freeze({
  LOBBY: 'LOBBY',
  PREFLIGHT_BLOCKED: 'PREFLIGHT_BLOCKED',
  STARTING: 'STARTING',
  READY: 'READY',
  PROBLEM_SELECTED: 'PROBLEM_SELECTED',
  CODING_REQUESTED: 'CODING_REQUESTED',
  CODING_OPEN: 'CODING_OPEN',
  SUBMISSION_PENDING: 'SUBMISSION_PENDING',
  WAITING_SYNC: 'WAITING_SYNC',
  SUBMISSION_RECORDED: 'SUBMISSION_RECORDED',
  RETURNING: 'RETURNING',
  COMPLETE: 'COMPLETE',
  RESULTS: 'RESULTS',
  CANCELLED: 'CANCELLED',
  VOIDED: 'VOIDED',
});

export const TAB_STATE = Object.freeze({
  TAB_NONE: 'TAB_NONE',
  TAB_OPENING: 'TAB_OPENING',
  TAB_OPEN: 'TAB_OPEN',
  TAB_CLOSE_AUTHORISED: 'TAB_CLOSE_AUTHORISED',
  TAB_VERIFYING: 'TAB_VERIFYING',
  TAB_ANNOUNCED: 'TAB_ANNOUNCED',
  TAB_CLOSING: 'TAB_CLOSING',
});

export const SERVER_CONNECTIVITY = Object.freeze({
  SERVER_ONLINE: 'SERVER_ONLINE',
  SERVER_LOST: 'SERVER_LOST',
  RECOVERING: 'RECOVERING',
});

export const EXT_CONNECTIVITY = Object.freeze({
  EXT_UNKNOWN: 'EXT_UNKNOWN',
  EXT_GUIDED: 'EXT_GUIDED',
  EXT_DEGRADED: 'EXT_DEGRADED',
});

/**
 * @param {{
 *   attempt?: {
 *     state?: string|null,
 *     window?: { effectiveDeadlineAt?: string|Date|null }|null,
 *     readiness?: { blockers?: string[] }|null,
 *     pendingUpload?: boolean,
 *   }|null,
 *   assessment?: { cancelled?: boolean, status?: string|null }|null,
 *   connectivity?: {
 *     server?: string,
 *     extension?: string,
 *   }|null,
 *   tabState?: string|null,
 *   localIntent?: string|null,
 *   now?: Date|number|null,
 *   pendingUpload?: boolean,
 * }} input
 * @returns {{
 *   flow: string,
 *   server: string,
 *   extension: string,
 * }}
 */
export function deriveExperienceState(input = {}) {
  const attempt = input.attempt || {};
  const assessment = input.assessment || {};
  const connectivity = input.connectivity || {};
  const tabState = input.tabState || TAB_STATE.TAB_NONE;
  const localIntent = input.localIntent || null;
  const pendingUpload = Boolean(input.pendingUpload ?? attempt.pendingUpload);
  const nowMs = toMs(input.now);

  const server = connectivity.server || SERVER_CONNECTIVITY.SERVER_ONLINE;
  const extension = connectivity.extension || EXT_CONNECTIVITY.EXT_UNKNOWN;

  // Terminal / override branches first (STATE-MACHINE §5).
  if (assessment.cancelled === true || assessment.status === 'cancelled') {
    return pack(FLOW_STATE.CANCELLED, server, extension);
  }
  if (attempt.state === 'voided') {
    return pack(FLOW_STATE.VOIDED, server, extension);
  }

  if (attempt.state === 'pending') {
    const blockers = attempt.readiness?.blockers || [];
    if (Array.isArray(blockers) && blockers.length > 0) {
      return pack(FLOW_STATE.PREFLIGHT_BLOCKED, server, extension);
    }
    return pack(FLOW_STATE.LOBBY, server, extension);
  }

  if (attempt.state === 'active') {
    const deadlineMs = toMs(attempt.window?.effectiveDeadlineAt);
    if (deadlineMs != null && nowMs != null && nowMs >= deadlineMs) {
      return pack(FLOW_STATE.COMPLETE, server, extension);
    }

    if (tabState === TAB_STATE.TAB_OPENING) {
      return pack(FLOW_STATE.CODING_REQUESTED, server, extension);
    }
    if (
      tabState === TAB_STATE.TAB_ANNOUNCED ||
      tabState === TAB_STATE.TAB_CLOSING ||
      tabState === TAB_STATE.TAB_CLOSE_AUTHORISED ||
      tabState === TAB_STATE.TAB_VERIFYING
    ) {
      return pack(FLOW_STATE.RETURNING, server, extension);
    }
    if (tabState === TAB_STATE.TAB_OPEN) {
      if (pendingUpload && server === SERVER_CONNECTIVITY.SERVER_LOST) {
        return pack(FLOW_STATE.WAITING_SYNC, server, extension);
      }
      if (pendingUpload) {
        return pack(FLOW_STATE.SUBMISSION_PENDING, server, extension);
      }
      return pack(FLOW_STATE.CODING_OPEN, server, extension);
    }

    // TAB_NONE (and any non-open tab state AO-3.1 cares about)
    if (pendingUpload && server === SERVER_CONNECTIVITY.SERVER_LOST) {
      return pack(FLOW_STATE.WAITING_SYNC, server, extension);
    }
    if (pendingUpload) {
      return pack(FLOW_STATE.SUBMISSION_PENDING, server, extension);
    }
    if (localIntent != null && localIntent !== '') {
      return pack(FLOW_STATE.PROBLEM_SELECTED, server, extension);
    }
    return pack(FLOW_STATE.READY, server, extension);
  }

  if (['submitted', 'expired', 'reconciling'].includes(attempt.state)) {
    return pack(FLOW_STATE.COMPLETE, server, extension);
  }
  if (attempt.state === 'finalised') {
    return pack(FLOW_STATE.RESULTS, server, extension);
  }

  // Exhaustive fallback — treat unknown as lobby-safe unavailable → LOBBY-equivalent
  return pack(FLOW_STATE.LOBBY, server, extension);
}

/**
 * Human-readable status label for container chrome (text, not colour alone).
 * @param {string} flow
 * @param {string} server
 */
export function experienceStatusLabel(flow, server) {
  if (server === SERVER_CONNECTIVITY.SERVER_LOST) {
    return 'Connection interrupted';
  }
  switch (flow) {
    case FLOW_STATE.LOBBY:
      return 'Waiting to start';
    case FLOW_STATE.PREFLIGHT_BLOCKED:
      return 'Setup needed';
    case FLOW_STATE.READY:
    case FLOW_STATE.PROBLEM_SELECTED:
    case FLOW_STATE.CODING_REQUESTED:
    case FLOW_STATE.CODING_OPEN:
      return 'In progress';
    case FLOW_STATE.SUBMISSION_PENDING:
      return 'Submission syncing';
    case FLOW_STATE.WAITING_SYNC:
      return 'Waiting to sync';
    case FLOW_STATE.SUBMISSION_RECORDED:
    case FLOW_STATE.RETURNING:
      return 'Submission recorded';
    case FLOW_STATE.COMPLETE:
      return 'Assessment ended';
    case FLOW_STATE.RESULTS:
      return 'Results available';
    case FLOW_STATE.CANCELLED:
      return 'Assessment cancelled';
    case FLOW_STATE.VOIDED:
      return 'Attempt voided';
    default:
      return 'Unavailable';
  }
}

function pack(flow, server, extension) {
  return { flow, server, extension };
}

function toMs(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
}
